import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { budgetService } from '../../services/budgetService';
import systemDataService from '../../services/systemDataService';
import FXRateService from '../../utils/fxRateService';
import FinanceKPICards from './FinanceKPICards';
import ActualsSection, { OUTGOING_TYPES, INCOMING_TYPES } from './ActualsSection';
import BreakdownSection from './BreakdownSection';
import InvoiceDatePicker from '../ui/InvoiceDatePicker';
import { CURRENCY_OPTIONS } from '../../utils/constants';
import { formatQuantityDisplay } from '../../utils/quantityDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSyncAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import './FinanceCalculator.css';

const ALL_DOC_TYPES = [...OUTGOING_TYPES.map(t => t.value), ...INCOMING_TYPES.map(t => t.value)];

const CLASSIFICATION_TO_BUDGET_SECTION = {
  FREIGHT: 'FREIGHT',
  COMMISSIONS: 'COMMISSIONS',
  PURCHASING_COSTS: 'PURCHASING_COSTS',
  LAYTIME: 'LAYTIME',
  FINANCING: 'FINANCING',
  OTHER_EXPENSES: 'OTHER_EXPENSES',
};

const FinanceCalculator = forwardRef(({
  deliveryId,
  budgetId,
  isExpanded,
  onToggle,
  onClose,
  isModal,
  showSuccess,
  showError,
  onBudgetLink,
  purchaseQuantity,
  purchaseUnit,
  saleQuantity,
  saleUnit,
  commodityType,
}, ref) => {
  const [lines, setLines] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [budgetHeaders, setBudgetHeaders] = useState([]);
  const [linkedBudget, setLinkedBudget] = useState(null);
  const [linkedBudgetError, setLinkedBudgetError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [fxLoadingMap, setFxLoadingMap] = useState({});
  const [cashflowHeaderId, setCashflowHeaderId] = useState(null);
  const [units, setUnits] = useState([]);
  const [belowGrossActuals, setBelowGrossActuals] = useState({});

  const isNewDelivery = !deliveryId || deliveryId === 'new' || deliveryId === 'undefined';
  const sectionRef = useRef(null);

  useImperativeHandle(ref, () => ({
    scrollIntoView: () => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    save: () => handleSave(false),
    hasUnsavedChanges: () => hasUnsavedChanges,
  }));

  const resolveUnitName = (unitValue) => {
    if (!unitValue) return 'MT';
    if (!isNaN(unitValue) && units.length > 0) {
      const unit = units.find(u => String(u.id) === String(unitValue));
      if (unit) return unit.name;
    }
    return isNaN(unitValue) ? unitValue : 'MT';
  };

  // ---- Load units on mount ----
  useEffect(() => {
    systemDataService.getUnits().then(data => {
      setUnits((data || []).filter(u => u.is_active));
    }).catch(() => {});
  }, []);

  // ---- Load data on expand ----
  useEffect(() => {
    if (isExpanded) {
      loadClassifications();
      loadBudgetHeaders();
      if (!isNewDelivery) {
        loadCashflowData();
      }
    }
  }, [isExpanded, deliveryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isExpanded && budgetId) {
      loadLinkedBudget(budgetId);
    } else {
      setLinkedBudget(null);
    }
  }, [isExpanded, budgetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClassifications = async () => {
    try {
      const data = await budgetService.listClassifications();
      setClassifications(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Failed to load classifications:', err);
    }
  };

  const loadBudgetHeaders = async () => {
    try {
      const params = commodityType ? { commodity_type: commodityType } : {};
      const data = await budgetService.listHeaders(params);
      setBudgetHeaders(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Failed to load budget headers:', err);
    }
  };

  const loadLinkedBudget = async (id) => {
    setLinkedBudgetError(null);
    try {
      const data = await budgetService.getHeader(id);
      setLinkedBudget(data);
    } catch (err) {
      console.error('Failed to load linked budget:', err);
      setLinkedBudget(null);
      const status = err.response?.status;
      if (status === 403 || status === 404) {
        const msg = status === 403
          ? 'You do not have permission to view this linked budget'
          : 'Linked budget could not be found (it may have been deleted)';
        setLinkedBudgetError(msg);
        if (showError) showError(msg);
      } else {
        setLinkedBudgetError('Failed to load the linked budget. Try refreshing.');
      }
    }
  };

  const loadCashflowData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/cashflow/`);
      const data = response.data;
      setCashflowHeaderId(data.id || null);

      const existingLines = (data.lines || [])
        .filter((l) => ALL_DOC_TYPES.includes(l.line_type))
        .map((l) => ({
          ...l,
          is_synced: l.is_synced || false,
          line_label: l.line_label || getDocTypeLabel(l.line_type),
          line_type_label: getDocTypeLabel(l.line_type),
          converted_amount_usd: l.converted_amount || 0,
          amount_open: computeAmountOpen(l),
        }));

      setLines(existingLines);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to load cashflow data:', err);
      }
      setLines([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getDocTypeLabel = (lineType) => {
    const all = [...OUTGOING_TYPES, ...INCOMING_TYPES];
    return all.find((t) => t.value === lineType)?.label || lineType;
  };

  const computeAmountOpen = (line) => {
    const inv = parseFloat(line.invoice_amount) || 0;
    const paid = parseFloat(line.amount_paid) || 0;
    return (inv - paid).toFixed(2);
  };

  // ---- Recalculate derived fields ----
  const recalcLine = useCallback((line) => {
    const inv = parseFloat(line.invoice_amount) || 0;
    const paid = parseFloat(line.amount_paid) || 0;
    return {
      ...line,
      amount_open: (inv - paid).toFixed(2),
    };
  }, []);

  // ---- FX conversion ----
  const triggerFxConversion = async (lineId, invoiceAmount, currency, invoiceDate) => {
    if (currency === 'USD') {
      setLines((prev) =>
        prev.map((l) =>
          l.id === lineId
            ? { ...l, converted_amount_usd: parseFloat(invoiceAmount) || 0, fx_rate_used: 1.0 }
            : l
        )
      );
      return;
    }

    setFxLoadingMap((prev) => ({ ...prev, [lineId]: true }));
    try {
      const date = invoiceDate || new Date().toISOString().split('T')[0];
      const result = await FXRateService.convertAmount(invoiceAmount || 0, date, currency, 'USD');
      setLines((prev) =>
        prev.map((l) =>
          l.id === lineId
            ? {
                ...l,
                converted_amount_usd: result.success ? result.convertedAmount : parseFloat(invoiceAmount) || 0,
                fx_rate_used: result.success ? result.fxRate : null,
              }
            : l
        )
      );
    } catch {
      setLines((prev) =>
        prev.map((l) =>
          l.id === lineId
            ? { ...l, converted_amount_usd: parseFloat(invoiceAmount) || 0, fx_rate_used: null }
            : l
        )
      );
    } finally {
      setFxLoadingMap((prev) => ({ ...prev, [lineId]: false }));
    }
  };

  const linesRef = useRef(lines);
  linesRef.current = lines;

  // ---- Line change handler ----
  const handleLineChange = useCallback(
    (lineId, updates) => {
      setHasUnsavedChanges(true);
      setLines((prev) => {
        const updated = prev.map((l) => {
          if (l.id !== lineId) return l;
          const merged = { ...l, ...updates };
          return recalcLine(merged);
        });
        return updated;
      });

      if ('invoice_currency' in updates || 'invoice_amount' in updates || 'invoice_date' in updates) {
        const line = linesRef.current.find((l) => l.id === lineId);
        if (line) {
          const merged = { ...line, ...updates };
          triggerFxConversion(lineId, merged.invoice_amount, merged.invoice_currency, merged.invoice_date);
        }
      }
    },
    [recalcLine] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleAddRow = useCallback((newLine) => {
    setHasUnsavedChanges(true);
    setLines((prev) => [...prev, newLine]);
  }, []);

  const handleRemoveRow = useCallback((lineId) => {
    setHasUnsavedChanges(true);
    setLines((prev) => prev.filter((l) => l.id !== lineId));
  }, []);

  const handleBelowGrossChange = useCallback((lineId, field, value) => {
    setHasUnsavedChanges(true);
    setBelowGrossActuals(prev => ({
      ...prev,
      [lineId]: { ...(prev[lineId] || {}), [field]: value }
    }));
  }, []);

  // ---- Budget linking ----
  const handleBudgetSelect = async (headerId) => {
    if (!headerId) {
      if (onBudgetLink) onBudgetLink(null);
      setLinkedBudget(null);
      return;
    }
    const id = parseInt(headerId, 10);
    if (onBudgetLink) onBudgetLink(id);
    await loadLinkedBudget(id);
  };

  // ---- Budget section sums ----
  const getBudgetSectionSum = (sectionCode) => {
    if (!linkedBudget?.lines) return 0;
    return linkedBudget.lines
      .filter((l) => l.section === sectionCode && !l.is_subtotal && !l.is_margin_row)
      .reduce((sum, l) => sum + (parseFloat(l.budget_amount) || 0), 0);
  };

  const getOutgoingBudgetTotal = () => getBudgetSectionSum('PRODUCT_SALES');
  const getIncomingBudgetTotal = () => getBudgetSectionSum('PRODUCT_PURCHASE');

  // ---- KPI calculations ----
  const outgoingLines = lines.filter((l) => OUTGOING_TYPES.some((t) => t.value === l.line_type));
  const incomingLines = lines.filter((l) => INCOMING_TYPES.some((t) => t.value === l.line_type));

  const sumField = (arr, field) => arr.reduce((s, l) => s + (parseFloat(l[field]) || 0), 0);
  const sumByType = (linesArr, lineType, field) =>
    linesArr.filter((l) => l.line_type === lineType).reduce((s, l) => s + (parseFloat(l[field]) || 0), 0);

  const outgoingTotal = sumField(outgoingLines, 'invoice_amount');
  const incomingTotal = sumField(incomingLines, 'invoice_amount');

  // KPI: Actual Margin = straight sum of all invoice amounts on live rows (Excel col H orange)
  const actualMargin = sumField(lines, 'invoice_amount');

  // Budget section sums
  const outgoingBudget = getOutgoingBudgetTotal();
  const incomingBudget = getIncomingBudgetTotal();
  const breakdownBudgetTotal = Object.values(CLASSIFICATION_TO_BUDGET_SECTION).reduce(
    (sum, sec) => sum + getBudgetSectionSum(sec),
    0
  );

  // Below-gross adjustment lines from linked budget (structured, not label-matched)
  const belowGrossLines = linkedBudget?.lines
    ? linkedBudget.lines.filter((l) => l.section === 'BELOW_GROSS_ADJUSTMENTS' && !l.is_margin_row)
    : [];
  const belowGrossBudget = belowGrossLines.reduce((s, l) => s + (parseFloat(l.budget_amount) || 0), 0);

  // KPI: Budget Margin = sum of all col M yellow cells (section headers + breakdown + below-gross)
  const budgetMargin = outgoingBudget + incomingBudget + breakdownBudgetTotal + belowGrossBudget;

  // KPI: Budget Variance = doc-type-aware formula per section (Excel col N blue)
  const outgoingVariance = outgoingBudget
    - sumByType(outgoingLines, 'PROFORMA_INVOICE', 'invoice_amount')
    - sumByType(outgoingLines, 'OUTGOING_INVOICE', 'invoice_amount')
    + sumByType(outgoingLines, 'CREDIT_NOTE', 'invoice_amount');
  const incomingVariance = incomingBudget
    - sumByType(incomingLines, 'PURCHASE_ORDER', 'invoice_amount')
    - sumByType(incomingLines, 'INCOMING_INVOICE', 'invoice_amount')
    + sumByType(incomingLines, 'DEBIT_NOTE', 'invoice_amount');
  const classificationVarianceTotal = Object.entries(CLASSIFICATION_TO_BUDGET_SECTION).reduce(
    (total, [clsCode, budgetSection]) => {
      const clsBudget = getBudgetSectionSum(budgetSection);
      const clsLines = lines.filter((l) => l.classification === clsCode);
      const clsVariance = clsBudget
        - sumByType(clsLines, 'PURCHASE_ORDER', 'invoice_amount')
        - sumByType(clsLines, 'INCOMING_INVOICE', 'invoice_amount')
        + sumByType(clsLines, 'DEBIT_NOTE', 'invoice_amount');
      return total + clsVariance;
    },
    0
  );
  const budgetVariance = outgoingVariance + incomingVariance + classificationVarianceTotal;

  // KPI: Cashflow = sum of amount_paid on document rows only (Excel col K green, excludes breakdown)
  const cashflowTotal = lines
    .filter((l) => ALL_DOC_TYPES.includes(l.line_type))
    .reduce((s, l) => s + (parseFloat(l.amount_paid) || 0), 0);

  // ---- Gross / Net margin ----
  // Gross = outgoing - incoming. Breakdown is a reclassified VIEW of the same invoices
  // (not separate costs), so incomingTotal already contains all cost invoices.
  const grossMargin = outgoingTotal - incomingTotal;

  // Net margin: gross +/- below-gross adjustments using structured net_margin_effect flags
  const netMarginAdjustment = belowGrossLines.reduce((adj, l) => {
    const amt = Math.abs(parseFloat(l.budget_amount) || 0);
    if (l.net_margin_effect === 'ADD_BACK') return adj + amt;
    return adj - amt;
  }, 0);
  const netMargin = grossMargin + netMarginAdjustment;

  // ---- Save ----
  const handleSave = async (submitWorkflow = false) => {
    if (isNewDelivery) {
      showError('Save the delivery first before saving finance calculator data');
      return;
    }

    setIsSaving(true);
    try {
      const linesToSave = lines
        .filter((l) => {
          return (
            l.is_synced ||
            (parseFloat(l.invoice_amount) || 0) > 0 ||
            (parseFloat(l.amount_paid) || 0) > 0 ||
            (l.partner || '').trim() !== '' ||
            (l.description || '').trim() !== '' ||
            l.due_date ||
            l.paid_date ||
            l.classification
          );
        })
        .map((l, idx) => ({
          ...(String(l.id).startsWith('fc_temp_') ? {} : { id: l.id }),
          line_type: l.line_type,
          classification: l.classification || null,
          partner: l.partner || '',
          partner_crm_id: l.partner_crm_id || null,
          description: l.description || '',
          source_invoice_id: l.source_invoice_id || null,
          source_invoice_type: l.source_invoice_type || null,
          is_synced: l.is_synced || false,
          invoice_date: l.invoice_date || null,
          invoice_currency: l.invoice_currency || 'USD',
          invoice_amount: parseFloat(l.invoice_amount) || 0,
          budget_amount: parseFloat(l.budget_amount) || 0,
          amount_paid: parseFloat(l.amount_paid) || 0,
          fx_rate_used: l.fx_rate_used != null ? parseFloat(l.fx_rate_used) || 0 : null,
          due_date: l.due_date || null,
          paid_date: l.paid_date || null,
          order: idx,
        }));

      const payload = { lines: linesToSave };

      if (cashflowHeaderId) {
        await axiosInstance.patch(`/api/deliveries/${deliveryId}/cashflow/`, payload);
      } else {
        await axiosInstance.post(`/api/deliveries/${deliveryId}/cashflow/`, payload);
      }

      await loadCashflowData();
      setHasUnsavedChanges(false);
      showSuccess(submitWorkflow ? 'Finance calculator submitted' : 'Finance calculator saved');
    } catch (err) {
      console.error('Save failed:', err);
      showError(err.response?.data?.error || 'Failed to save finance calculator data');
    } finally {
      setIsSaving(false);
    }
  };

  const renderColumnHeaders = () => (
    <tr className="fc-column-header">
      <th className="fc-col-type">Type</th>
      <th className="fc-col-classify">Classify</th>
      <th className="fc-col-partner">Partner</th>
      <th className="fc-col-desc">Description</th>
      <th className="fc-col-inv-date">Inv. Date</th>
      <th className="fc-col-inv-curr">Curr.</th>
      <th className="fc-col-amt-unit fc-amount-col">Amt/Unit</th>
      <th className="fc-col-inv-amt fc-amount-col">Inv. Amt</th>
      <th className="fc-col-amt-usd fc-amount-col">USD</th>
      <th className="fc-col-fx fc-amount-col">FX</th>
      <th className="fc-col-amt-paid fc-amount-col">Paid/Rec.</th>
      <th className="fc-col-amt-open fc-amount-col">Open</th>
      <th className="fc-col-budget fc-amount-col">Budget</th>
      <th className="fc-col-variance fc-amount-col">Variance</th>
      <th className="fc-col-due">Due</th>
      <th className="fc-col-payment">Paid</th>
      <th className="fc-col-actions"></th>
    </tr>
  );

  // ---- Shared body content ----
  const renderBody = () => (
    <div className="fc-body">
      {isLoading ? (
        <div className="fc-loading">Loading finance calculator...</div>
      ) : (
        <div className="fc-sections">
          {/* Details card */}
          <div className="fc-section-card">
            <div className="fc-section-card-header">
              <h3>Calculator Details</h3>
              {linkedBudget && (
                <span className="fc-budget-linked-badge">
                  <i className="fas fa-link"></i> Linked
                </span>
              )}
            </div>
            <div className="fc-details-body">
              <div className="fc-field-group">
                <label>Purchase Qty</label>
                <span className={`fc-field-value ${!purchaseQuantity ? 'fc-empty' : ''}`}>
                  {purchaseQuantity
                    ? <>{formatQuantityDisplay(purchaseQuantity)} <span className="fc-unit">{resolveUnitName(purchaseUnit)}</span></>
                    : 'No recap linked'}
                </span>
              </div>
              <div className="fc-field-group">
                <label>Sale Qty</label>
                <span className={`fc-field-value ${!saleQuantity ? 'fc-empty' : ''}`}>
                  {saleQuantity
                    ? <>{formatQuantityDisplay(saleQuantity)} <span className="fc-unit">{resolveUnitName(saleUnit)}</span></>
                    : 'No recap linked'}
                </span>
              </div>
              <div className="fc-budget-link-group">
                <label>Linked Budget</label>
                <div className="fc-budget-link-row">
                  <select
                    value={budgetId != null ? String(budgetId) : ''}
                    onChange={(e) => handleBudgetSelect(e.target.value)}
                    className="fc-budget-select"
                  >
                    <option value="">-- No budget linked --</option>
                    {budgetHeaders.map((bh) => {
                      const dn = bh.offer_group_identifier ? ` [${bh.offer_group_identifier}]` : '';
                      return (
                        <option key={bh.id} value={String(bh.id)}>
                          {bh.display_name || 'Untitled'}{dn} ({bh.currency_code}) - {bh.status}
                        </option>
                      );
                    })}
                  </select>
                  {budgetId && (
                    <button
                      type="button"
                      className="fc-budget-refresh-btn"
                      title="Refresh budget data"
                      onClick={() => loadLinkedBudget(budgetId)}
                    >
                      <FontAwesomeIcon icon={faSyncAlt} />
                    </button>
                  )}
                </div>
                {budgetId && !linkedBudget && linkedBudgetError && (
                  <div className="fc-budget-link-error">{linkedBudgetError}</div>
                )}
                {!budgetId && budgetHeaders.length > 0 && (
                  <div className="fc-budget-link-hint">
                    Pick a budget above so amounts pull through.
                  </div>
                )}
                {!budgetId && budgetHeaders.length === 0 && (
                  <div className="fc-budget-link-hint">
                    No budgets available. Create one from Trade Operations &gt; Budget Calculator.
                  </div>
                )}
              </div>
              <FinanceKPICards
                grossMargin={grossMargin}
                netMargin={netMargin}
                budgetMargin={budgetMargin}
                actualMargin={actualMargin}
                budgetVariance={budgetVariance}
                cashflow={cashflowTotal}
              />
            </div>
          </div>

          {/* Outgoing card */}
          <div className="fc-section-card">
            <div className="fc-section-card-header">
              <h3>Outgoing Invoices / Credit Notes</h3>
            </div>
            <div className="fc-grid-container">
              <table className="fc-grid">
                <thead>{renderColumnHeaders()}</thead>
                <tbody>
                  <ActualsSection
                    sectionType="OUTGOING"
                    lines={lines}
                    classifications={classifications}
                    budgetSectionTotal={getOutgoingBudgetTotal()}
                    quantity={saleQuantity}
                    onLineChange={handleLineChange}
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    fxLoadingMap={fxLoadingMap}
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* Incoming card */}
          <div className="fc-section-card">
            <div className="fc-section-card-header">
              <h3>Incoming Invoices / Debit Notes</h3>
            </div>
            <div className="fc-grid-container">
              <table className="fc-grid">
                <thead>{renderColumnHeaders()}</thead>
                <tbody>
                  <ActualsSection
                    sectionType="INCOMING"
                    lines={lines}
                    classifications={classifications}
                    budgetSectionTotal={getIncomingBudgetTotal()}
                    quantity={purchaseQuantity}
                    onLineChange={handleLineChange}
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    fxLoadingMap={fxLoadingMap}
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* 3rd party costs card */}
          <div className="fc-section-card">
            <div className="fc-section-card-header fc-breakdown-parent">
              <h3>3rd party costs</h3>
            </div>
            <div className="fc-grid-container">
              <table className="fc-grid">
                <thead>{renderColumnHeaders()}</thead>
                <tbody>
                  {classifications.map((cls) => {
                    const classified = lines.filter((l) => l.classification === cls.code);
                    const budgetSec = CLASSIFICATION_TO_BUDGET_SECTION[cls.code];
                    const budgetAmt = budgetSec ? getBudgetSectionSum(budgetSec) : 0;
                    const budgetSubItems = linkedBudget?.lines
                      ? linkedBudget.lines.filter(
                          (l) => l.section === budgetSec && !l.is_subtotal && !l.is_margin_row
                        )
                      : [];
                    return (
                      <BreakdownSection
                        key={cls.code}
                        classificationCode={cls.code}
                        classificationLabel={cls.label}
                        classifiedLines={classified}
                        budgetAmount={budgetAmt}
                        budgetSubItems={budgetSubItems}
                        quantity={purchaseQuantity}
                      />
                    );
                  })}

                </tbody>
              </table>
            </div>
          </div>

          {/* Margin & Adjustments card */}
          <div className="fc-section-card">
            <div className="fc-section-card-header fc-margin-card-header">
              <h3>Gross Margin / Adjustments / Net Margin</h3>
            </div>
            <div className="fc-margin-body">
              {/* Gross Margin */}
              <div className="fc-margin-summary-row fc-gross-margin-row">
                <span className="fc-margin-label">Gross Margin:</span>
                <span className="fc-margin-formula">Rev - Ex (amounts)</span>
                <span className="fc-margin-value">{formatAmt(grossMargin)}</span>
              </div>

              {/* Below-gross adjustment rows */}
              {belowGrossLines.length > 0 && (
                <table className="fc-margin-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Date</th>
                      <th>Currency</th>
                      <th>Amount</th>
                      <th>Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {belowGrossLines.map((adjLine) => {
                      const labelLower = (adjLine.line_label || '').toLowerCase();
                      const isInterest = labelLower.includes('interest');
                      const isTravel = labelLower.includes('travel');
                      const actuals = belowGrossActuals[adjLine.id] || {};

                      return (
                        <tr key={`adj-${adjLine.id}`} className={isInterest ? 'fc-interest-row' : isTravel ? 'fc-travel-row' : ''}>
                          <td className="fc-margin-type-cell">
                            {adjLine.line_label}
                          </td>
                          <td>
                            {isInterest ? (
                              <textarea
                                value={actuals.description || ''}
                                onChange={(e) => handleBelowGrossChange(adjLine.id, 'description', e.target.value)}
                                className="fc-input fc-description-textarea"
                                placeholder="Description"
                                rows={1}
                              />
                            ) : isTravel ? (
                              <span className="fc-accounting-label">Accounting</span>
                            ) : (
                              <span>{adjLine.description || ''}</span>
                            )}
                          </td>
                          <td>
                            {isInterest ? (
                              <InvoiceDatePicker
                                name={`adj_date_${adjLine.id}`}
                                value={actuals.invoice_date || ''}
                                onChange={(e) => handleBelowGrossChange(adjLine.id, 'invoice_date', e.target.value)}
                                className="fc-date-picker"
                              />
                            ) : isTravel ? (
                              <span className="fc-accounting-label">Accounting</span>
                            ) : null}
                          </td>
                          <td>
                            {isInterest ? (
                              <select
                                value={actuals.invoice_currency || 'USD'}
                                onChange={(e) => handleBelowGrossChange(adjLine.id, 'invoice_currency', e.target.value)}
                                className="fc-input fc-currency-select"
                              >
                                {CURRENCY_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.value}</option>
                                ))}
                              </select>
                            ) : isTravel ? (
                              <span className="fc-accounting-label">Accounting</span>
                            ) : null}
                          </td>
                          <td className="fc-amount-cell">
                            {isInterest ? (
                              <input
                                type="text"
                                inputMode="decimal"
                                value={actuals.invoice_amount ?? ''}
                                onChange={(e) => handleBelowGrossChange(adjLine.id, 'invoice_amount', e.target.value)}
                                className="fc-input fc-amount-input"
                                placeholder="0.00"
                              />
                            ) : isTravel ? (
                              <span className="fc-accounting-label">Accounting</span>
                            ) : null}
                          </td>
                          <td className="fc-amount-cell fc-budget-highlight">
                            {formatAmt(Math.abs(parseFloat(adjLine.budget_amount) || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Net Margin */}
              <div className="fc-margin-summary-row fc-net-margin-row">
                <span className="fc-margin-label">Budget Net Margin:</span>
                <span className="fc-margin-formula">
                  {outgoingTotal > 0 ? `${((netMargin / outgoingTotal) * 100).toFixed(1)}%` : '-'}
                </span>
                <span className="fc-margin-value">{formatAmt(netMargin)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ---- Modal render ----
  if (isModal) {
    if (!isExpanded) return null;
    return (
      <div className="fc-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
        <div className="fc-container" ref={sectionRef} id="finance-calculator-section">
          <div className="fc-modal-header">
            <span className="fc-modal-header-left">
              <span>Finance Calculator</span>
              {hasUnsavedChanges && <span className="fc-unsaved-badge">Unsaved</span>}
            </span>
            <span className="fc-toggle-right">
              <span
                className="fc-refresh-btn"
                title="Refresh from invoices"
                onClick={() => loadCashflowData()}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={isLoading ? 'fa-spin' : ''} />
              </span>
              <button className="fc-modal-close-btn" onClick={onClose} title="Close" type="button">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          </div>
          {renderBody()}
        </div>
      </div>
    );
  }

  // ---- Inline (legacy) render ----
  return (
    <div className="fc-container" ref={sectionRef} id="finance-calculator-section">
      <button className="fc-collapse-toggle" onClick={onToggle} type="button">
        <span>Finance Calculator</span>
        <span className="fc-toggle-right">
          {hasUnsavedChanges && <span className="fc-unsaved-badge">Unsaved</span>}
          {isExpanded && (
            <span
              className="fc-refresh-btn"
              title="Refresh from invoices"
              onClick={(e) => {
                e.stopPropagation();
                loadCashflowData();
              }}
            >
              <FontAwesomeIcon icon={faSyncAlt} className={isLoading ? 'fa-spin' : ''} />
            </span>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`fc-collapse-icon ${isExpanded ? 'expanded' : ''}`}
          />
        </span>
      </button>

      {isExpanded && renderBody()}
    </div>
  );
});

function formatAmt(val) {
  if (val == null || val === '') return '0.00';
  const n = parseFloat(val);
  if (isNaN(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

FinanceCalculator.displayName = 'FinanceCalculator';

export default FinanceCalculator;
